import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {RESOURCES_DATA_SERVICE, ResourcesDataService} from '../../../../services/resources/resources-data.service';
import { ResourceCountry, ResourceProvinces } from '../../../../models/resources/resources.model';
import {IAddressData, LeadData} from '../../../../models/leads/leads.model';
import {LEADS_DATA_SERVICE, LeadsDataService} from '../../../../services/leads/leads-data.service';
import {AddressHelper} from '../../../../helper/address.helper';
import { ValidatorHelper } from '../../../../helper/validator.helper';
import { Subscription } from 'rxjs';


@Component({
  selector: 'fi-address-editor',
  templateUrl: './address-editor.component.html',
  styleUrls: ['./address-editor.component.scss'],
  standalone: false,
})
export class AddressEditorComponent implements OnInit, OnChanges, OnDestroy {

  resourceCountries: ResourceCountry[];
  resourceProvinces: ResourceProvinces[];
  provinceCountryCodes: string[] = [];
  sameAsContactAddress = true;
  private formSubscription: Subscription;

  @Input() form = this.fb.group({
    Commission: this.fb.control(''),
    Address: this.fb.control('', Validators.pattern(ValidatorHelper.addressValidator)),
    ZipCode: this.fb.control('', Validators.pattern(ValidatorHelper.zipValidator)),
    City: this.fb.control('', Validators.pattern(ValidatorHelper.cityValidator)),
    CountryCode: this.fb.control(''),
    Website: this.fb.control('', Validators.pattern(ValidatorHelper.urlValidator)),
    AddressExt: this.fb.control('', Validators.pattern(ValidatorHelper.addressValidator)),
    Email: this.fb.control(''),
    Phone: this.fb.control(''),
    PhonePrefix: this.fb.control(''),
    Mobile: this.fb.control(''),
    MobilePrefix: this.fb.control(''),
    ProvinceCode: this.fb.control(''),
  });

  @Input() contactForm?: UntypedFormGroup;
  @Input() leadDetails: LeadData;
  @Output() addressUpdated = new EventEmitter<IAddressData>();
  @Output() onSameAsContactAddress = new EventEmitter<boolean>();

  constructor(private fb: UntypedFormBuilder,
              @Inject(LEADS_DATA_SERVICE) private leadDataService: LeadsDataService,
              @Inject(RESOURCES_DATA_SERVICE) private resourcesDataService: ResourcesDataService) {

    AddressHelper.getCountries().then(data => {
      this.resourceCountries = data;
    });

    AddressHelper.getProvinceCountryCodes().then(data => {
      this.provinceCountryCodes = data;
    });
  }

  ngOnInit(): void {
    this.onCountryChange(this.form?.controls?.CountryCode?.value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.contactForm?.currentValue !== changes.contactForm?.previousValue) {
      this.formSubscription?.unsubscribe();
      this.onUpdateContactForm(this.contactForm?.getRawValue());
      this.formSubscription = this.contactForm?.valueChanges.subscribe((data) => this.onUpdateContactForm(data));
    }

    if (!changes.leadDetails?.previousValue && changes.leadDetails?.currentValue &&
      changes.leadDetails.currentValue.Lead.Address) {
      this.form.patchValue({
        ...changes.leadDetails.currentValue.Lead.Address,
        Commission: changes.leadDetails.currentValue.Lead.Commission
      });
      this.onCountryChange(this.form?.controls?.CountryCode?.value);
    }
    if(!changes.leadDetails?.previousValue && changes.leadDetails?.currentValue && changes.leadDetails.currentValue.Lead && changes.leadDetails.currentValue.Lead.Commission){
      this.form.patchValue({
        Commission: changes.leadDetails.currentValue.Lead.Commission
      });
    }

    if (changes.form?.currentValue) {
      this.onCountryChange(this.form?.controls?.CountryCode?.value);
    }
  }

  onCountryChange(countryCode: string): void {
    this.form.controls.CountryCode.setValue(countryCode);

    this.form.controls.ProvinceCode.setValue('');

    AddressHelper.getProvinces(countryCode).then(data => {
      this.resourceProvinces = data;
    })
  }

  getResourceProvinces(countryCode: string): void {
    AddressHelper.getProvinces(countryCode).then(data => {
      this.resourceProvinces = data;
    })
  }

  private onUpdateContactForm(rawData?: {[key: string] : any}): void {
    if (!rawData || !this.sameAsContactAddress) {
      return;
    }

    const oldCountry = this.form?.controls?.CountryCode?.value;
    const contactCountry = rawData?.CountryCode;

    if (oldCountry !== contactCountry) {
      this.getResourceProvinces(contactCountry);
    }

    this.form?.patchValue(rawData);
  }


  onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    const body = this.form.getRawValue();

    this.leadDataService.updateCommission(this.leadDetails.Lead.Id, {Commission: body.Commission}).subscribe(({data, success}) => {
      this.addressUpdated.emit(body);
    });
    this.leadDataService.updateAddress(this.leadDetails.Lead.Id, this.form.getRawValue()).subscribe(({data, success}) => {
      if (success) {
        this.addressUpdated.emit(body);
      }
    });
  }

  onDelete(): void {

    this.leadDataService.deleteAddress(this.leadDetails.Lead.Id).subscribe(({data, success}) => {
      if (success) {
        const body = this.form.getRawValue();
        body.Address = '';
        body.CountryCode = '';
        body.Commission = '';
        body.City = '';
        body.ZipCode = '';
        this.addressUpdated.emit(body);
      }
    });
  }

  onSameAsContactAddressChange(): void {
    this.onSameAsContactAddress.emit(this.sameAsContactAddress);

    if (!this.sameAsContactAddress) {
      for (const key in this.form.controls) {
        if (key !== 'Commission') {
          this.form.controls[key].setValue('');
        }
      }
      this.onCountryChange('');
    } else {
      this.onUpdateContactForm(this.contactForm?.getRawValue());
    }
  }

  ngOnDestroy(): void {
    this.formSubscription?.unsubscribe();
  }
}
